import { cn } from '@heidi/utils';

import { typographyVariant } from '../typography';

/**
 * Base styles for SpotlightContent (the floating card container)
 *
 * Figma reference: w-62 (248px), rounded-md, bg-background-primary (dark maroon)
 * Entire tooltip uses dark background with white text
 */
export const spotlightContentStyles = cn(
  // Layout & sizing (Figma: 248px width, flex column with gap)
  'z-50 w-62',
  'flex flex-col gap-3 py-3',
  // Card-like appearance (Figma: rounded-md, spotlight background, shadow)
  'rounded-md border-0 bg-callout-background shadow-callout',
  // Text - white on dark background
  'text-text-primary-on-dark',
  // Focus
  'outline-none',
  // Animations (origin-aware)
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2',
  'data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2',
  'data-[side=top]:slide-in-from-bottom-2'
);

/**
 * Styles for SpotlightHeader
 *
 * Figma reference: Horizontal layout for icon + title + close button
 * Background inherited from SpotlightContent (bg-background-primary)
 */
export const spotlightHeaderStyles = cn('flex items-center gap-2', 'px-4 py-0.5');

/**
 * Styles for SpotlightHeader icon
 *
 * Consistent icon sizing (14px) matching p2Bold text size.
 * Uses shrink-0 to prevent icon from compressing.
 */
export const spotlightHeaderIconStyles = cn(
  'shrink-0',
  '[&>svg]:size-3.5',
  'text-text-primary-on-dark'
);

/**
 * Styles for SpotlightTitle
 *
 * Figma reference: Body/Single/P2 Medium - text-sm, font-medium, leading-none
 * Uses p2Bold variant with leading-none override for single-line style
 */
export const spotlightTitleStyles = cn(
  typographyVariant({ variant: 'p2Bold' }),
  'flex-1 leading-none',
  'text-text-primary-on-dark'
);

/**
 * Styles for SpotlightDescription
 *
 * Figma reference: Body/Paragraph/Caption - text-xs, font-normal, leading-snug (1.375)
 * Uses caption variant for consistent typography
 */
export const spotlightDescriptionStyles = cn(
  typographyVariant({ variant: 'caption' }),
  'px-4 py-1',
  'text-text-primary-on-dark',
  'text-pretty'
);

/**
 * Styles for SpotlightBody
 *
 * Figma reference: Full-width content area (e.g., for images)
 * No horizontal padding to allow edge-to-edge content
 */
export const spotlightBodyStyles = cn('w-full');

/**
 * Styles for SpotlightImage
 *
 * Figma reference: Image container with 992:576 aspect ratio (~16:9)
 * Full-width, edge-to-edge display. Children (img/video) should use object-cover.
 */
export const spotlightImageStyles = cn(
  'relative w-full overflow-hidden',
  'aspect-video',
  // Ensure child images and videos fill the container
  '[&>img]:absolute [&>img]:inset-0 [&>img]:size-full [&>img]:object-cover',
  '[&>video]:absolute [&>video]:inset-0 [&>video]:size-full [&>video]:object-cover'
);

/**
 * Styles for SpotlightFooter
 *
 * Figma reference: Right-aligned button row with optional progress counter
 */
export const spotlightFooterStyles = cn('flex items-center gap-2', 'px-4 pb-1');

/**
 * Styles for SpotlightProgress
 *
 * Figma reference: Body/Paragraph/Caption - text-xs, font-normal, leading-snug (1.375)
 * Uses caption variant, takes remaining space to push buttons right
 */
export const spotlightProgressStyles = cn(
  typographyVariant({ variant: 'caption' }),
  'flex-1',
  'text-text-primary-on-dark'
);

/**
 * Styles for SpotlightCloseButton
 *
 * Uses white/opacity overlays for hover/active states so the button
 * blends naturally with the dark spotlight background.
 */
export const spotlightCloseButtonStyles = cn(
  'bg-transparent text-text-primary-on-dark',
  'hover:bg-white/15 hover:text-text-primary-on-dark hover:shadow-none',
  'active:bg-white/25',
  'disabled:bg-transparent disabled:text-text-disabled',
  '[&_svg]:size-3.5 [&_svg]:shrink-0'
);
